import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { FTERule } from "@/hooks/useAdminPayroll";
import { PayrollPosition } from "@/types/payroll-admin";
import { FTE_CONDITION_OPTIONS } from "@/constants/adminPayroll";

interface FTERulesTabProps {
  positions: PayrollPosition[];
  fteRules: FTERule[];
  onAddRule: (rule: Omit<FTERule, "id" | "created_at" | "updated_at">) => void;
  onUpdateRule: (id: string, updates: Partial<FTERule>) => void;
  onDeleteRule: (id: string) => void;
}

const emptyRuleForm = {
  position_id: "",
  condition_type: "always",
  condition_value: {},
  min_fte: 1,
  per_rooms: 0,
  notes: "",
};

export function FTERulesTab({
  positions,
  fteRules,
  onAddRule,
  onUpdateRule,
  onDeleteRule,
}: FTERulesTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyRuleForm);
  const [editingRule, setEditingRule] = useState<FTERule | null>(null);

  const getPositionName = (id: string) =>
    positions.find((p) => p.id === id)?.name || id;

  const getConditionLabel = (value: string) => {
    const option = FTE_CONDITION_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const getPositionById = (id: string) => positions.find((p) => p.id === id);

  const formatConditionDisplay = (rule: FTERule) => {
    return getConditionLabel(rule.condition_type);
  };

  const formatFTEFormula = (rule: FTERule) => {
    if (rule.per_rooms && rule.per_rooms > 0) {
      return `max(${rule.min_fte}, rooms ÷ ${rule.per_rooms})`;
    }
    return `${rule.min_fte} FTE`;
  };

  const openAddDialog = () => {
    setFormData(emptyRuleForm);
    setEditingRule(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (rule: FTERule) => {
    setFormData({
      position_id: rule.position_id,
      condition_type: rule.condition_type,
      condition_value: rule.condition_value,
      min_fte: rule.min_fte,
      per_rooms: rule.per_rooms || 0,
      notes: rule.notes,
    });
    setEditingRule(rule);
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.position_id) {
      toast.error("Please select a position");
      return;
    }

    if (editingRule) {
      onUpdateRule(editingRule.id, {
        position_id: formData.position_id,
        condition_type: formData.condition_type,
        condition_value: formData.condition_value,
        min_fte: formData.min_fte,
        per_rooms: formData.per_rooms || null,
        notes: formData.notes,
      });
      toast.success("Rule updated");
    } else {
      onAddRule({
        position_id: formData.position_id,
        condition_type: formData.condition_type,
        condition_value: formData.condition_value,
        min_fte: formData.min_fte,
        per_rooms: formData.per_rooms || null,
        notes: formData.notes,
        is_active: true,
      });
      toast.success("Rule added");
    }

    resetForm();
    setIsDialogOpen(false);
  };

  const resetForm = () => {
    setFormData(emptyRuleForm);
    setEditingRule(null);
  };

  const handleFormChange = (
    field: keyof typeof formData,
    value: string | number | any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderRuleForm = () => {
    const selectedPosition = getPositionById(formData.position_id);

    return (
      <>
        <div className="space-y-2">
          <Label htmlFor="rule-position">Position</Label>
          <Select
            value={formData.position_id}
            onValueChange={(v) => handleFormChange("position_id", v)}
          >
            <SelectTrigger id="rule-position" className="justify-between">
              <span>
                {selectedPosition ? selectedPosition.name : "Select position"}
              </span>
            </SelectTrigger>
            <SelectContent>
              {positions
                .filter((p) => p.is_active)
                .map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="rule-condition">Condition</Label>
          <Select
            value={formData.condition_type}
            onValueChange={(v) => handleFormChange("condition_type", v)}
          >
            <SelectTrigger id="rule-condition" className="justify-between">
              <span>
                {getConditionLabel(formData.condition_type) ||
                  "Select condition"}
              </span>
            </SelectTrigger>
            <SelectContent>
              {FTE_CONDITION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="rule-minFTEs">Min FTEs</Label>
            <Input
              id="rule-minFTEs"
              type="number"
              min="0"
              step="0.5"
              value={formData.min_fte}
              onChange={(e) =>
                handleFormChange("min_fte", Number(e.target.value))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rule-perRooms">Variable: 1 FTE per X rooms</Label>
            <Input
              id="rule-perRooms"
              type="number"
              min="0"
              value={formData.per_rooms}
              onChange={(e) =>
                handleFormChange("per_rooms", Number(e.target.value))
              }
              placeholder="e.g., 15"
            />
          </div>
        </div>

        <div className="p-3 bg-muted/50 rounded-lg text-sm">
          <span className="font-medium">Formula: </span>
          {formData.per_rooms > 0
            ? `max(${formData.min_fte}, rooms ÷ ${formData.per_rooms})`
            : `${formData.min_fte} FTE (fixed)`}
        </div>

        <div className="space-y-2">
          <Label htmlFor="rule-notes">Notes</Label>
          <Input
            id="rule-notes"
            value={formData.notes}
            onChange={(e) => handleFormChange("notes", e.target.value)}
            placeholder="e.g., Night coverage"
          />
        </div>
      </>
    );
  };

  const handleDeleteRule = (id: string) => {
    onDeleteRule(id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">FTE Calculation Rules</h2>
        <Button className="bg-primary-blue text-white" onClick={openAddDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Add Rule
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Position</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>FTE Formula</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fteRules
                .filter((r) => r.is_active)
                .map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-medium">
                      {getPositionName(rule.position_id)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {formatConditionDisplay(rule)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatFTEFormula(rule)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {rule.notes}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(rule)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRule(rule.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="p-0 max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {editingRule ? "Edit FTE Rule" : "Add FTE Rule"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto minimal-scrollbar px-6">
            {editingRule && (
              <div className="p-3 bg-muted rounded-lg mb-4">
                <div className="text-sm text-muted-foreground">Position</div>
                <div className="font-medium">
                  {getPositionName(editingRule.position_id)}
                </div>
              </div>
            )}

            <div className="space-y-4 py-4">{renderRuleForm()}</div>
          </div>

          <div className="px-6 py-4 shrink-0">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary-blue text-white"
                onClick={handleSubmit}
              >
                {editingRule ? "Save" : "Add Rule"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
